import { Box, Button, Tag, Text } from '@zillow/constellation-10';
import Image from 'next/image';
import { LINKS } from '../../constants';
import { emitCreditClimbEnroll } from '../../analytics/events';
import { YourRentalPageTriggerObject, YourRentalPageTriggerSource } from '../../analytics/events';
import CreditScoreIcon from '../../assets/CreditScore.svg';
import CreditScoreMobileIcon from '../../assets/CreditScoreMobile.svg';
import { RequestZillowPaymentsModalCta } from '../Modals';
import { useAddCurrentQueryParams, useIsInMobileWebView } from '../../hooks';
import { useYourRentalPageContext } from '../../contexts/YourRentalPageContext';

const CONTENT = {
  TITLE_1: 'Build credit',
  TITLE_2: ' when you pay rent',
  TOP_BODY:
    'With CreditClimb, your on-time payments can be added to your credit report, just like loan or credit card payments. It’s a simple way to build credit history while you rent.',
  UPSELL_TITLE: 'Choose the option that works for you',
  PAYMENTS_TAG: 'Free',
  PAYMENTS_BODY:
    'Ask your landlord to use Zillow payments and get access to rent reporting. Payments are free when you pay with a direct bank transfer.',
  PAYMENTS_CTA: 'Request Zillow payments',
  CREDIT_TAG: '$20 yearly',
  CREDIT_BODY:
    'Join over 5 million renters who are enrolled in a credit building membership with Zillow’s trusted partner, Esusu.',
  CREDIT_CTA: 'Enroll now',
} as const;

const CreditScoreImage = () => {
  return (
    <Box
      css={{
        padding: { base: 0, lg: 'tight' },
        order: { base: 1, lg: 2 },
      }}
    >
      <Box css={{ display: { base: 'block', lg: 'none' } }}>
        <Image src={CreditScoreMobileIcon} alt="Credit score" width={120} height={120} />
      </Box>
      <Box css={{ display: { base: 'none', lg: 'block' } }}>
        <Image src={CreditScoreIcon} alt="Credit score" width={160} height={160} />
      </Box>
    </Box>
  );
};

const Title = () => {
  return (
    <Text textStyle="heading-md">
      <span
        style={{
          color: 'var(--text-express-insight-hero, #136F65)',
        }}
      >
        {CONTENT.TITLE_1}
      </span>{' '}
      {CONTENT.TITLE_2}
    </Text>
  );
};

type UpsellCardProps = {
  tag: React.ReactNode;
  body: string;
  cta?: string;
  ctaLink?: string;
  dataTestId: string;
  onClick?: () => void;
  customCta?: React.ReactNode;
};

const UpsellCard = ({ tag, body, cta, ctaLink, dataTestId, onClick, customCta }: UpsellCardProps) => {
  const isInMobileWebView = useIsInMobileWebView();
  const addQueryParams = useAddCurrentQueryParams();

  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'tight',
        padding: 'default',
        border: '{borderWidths.default} solid {colors.gray.300}',
        borderRadius: '{radii.300}',
        alignItems: 'flex-start',
        height: '100%',
      }}
      data-testid={dataTestId}
    >
      {tag}
      <Text>{body}</Text>
      <Box css={{ marginTop: 'auto' }}>
        {customCta || (
          <Button asChild emphasis="filled" size="sm" onClick={onClick}>
            <a
              href={addQueryParams(ctaLink ?? '')}
              {...(!isInMobileWebView && { target: '_blank', rel: 'noreferrer' })}
            >
              {cta}
            </a>
          </Button>
        )}
      </Box>
    </Box>
  );
};

type PaymentsUpsellProps = {
  listingAlias?: string;
};

const PaymentsUpsell = ({ listingAlias }: PaymentsUpsellProps) => {
  const { renterProfile } = useYourRentalPageContext();

  return (
    <>
      <Box
        css={{
          display: 'flex',
          flexDirection: { base: 'column', lg: 'row' },
          justifyContent: 'space-between',
          alignItems: { base: 'flex-start', lg: 'center' },
          padding: { base: 'default', lg: '{spacing.looser} {spacing.layout.looser}' },
          backgroundColor: 'var(--bg-softest, #EEEEF0)',
          borderRadius: '{radii.300} {radii.300} 0 0',
        }}
        data-testid="RentReporting-TopSection"
      >
        <Box
          css={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            flex: 1,
            order: { base: 2, lg: 1 },
          }}
        >
          <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
            <Title />
            <Text>{CONTENT.TOP_BODY}</Text>
          </Box>
        </Box>
        <CreditScoreImage />
      </Box>
      <Box
        css={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          flex: 1,
          order: { base: 2, lg: 1 },
          padding: { base: 'default', lg: '{spacing.loose} {spacing.layout.looser}' },
        }}
        data-testid="RentReporting-UpsellSection"
      >
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
          <Text textStyle="body-lg-bold">{CONTENT.UPSELL_TITLE}</Text>
          <Box
            css={{
              display: 'grid',
              width: '100%',
              gridTemplateColumns: { base: '1fr', lg: 'repeat(2, 1fr)', xxl: 'repeat(3, 1fr)' },
              gap: 'tight',
            }}
          >
            <UpsellCard
              tag={<Tag tone="green">{CONTENT.PAYMENTS_TAG}</Tag>}
              body={CONTENT.PAYMENTS_BODY}
              dataTestId="RentReporting-PaymentsUpsell"
              customCta={
                <RequestZillowPaymentsModalCta
                  ctaText={CONTENT.PAYMENTS_CTA}
                  clickstreamTriggerObject={YourRentalPageTriggerObject.ZRM_PAYMENTS_UPSELL}
                  authTriggerSource={YourRentalPageTriggerSource.REQUEST_ZILLOW_PAYMENTS}
                  listingAlias={listingAlias}
                />
              }
            />
            <UpsellCard
              tag={<Tag tone="yellow">{CONTENT.CREDIT_TAG}</Tag>}
              body={CONTENT.CREDIT_BODY}
              cta={CONTENT.CREDIT_CTA}
              ctaLink={LINKS.RENTAL_CREDIT_CLIMB_ENROLL}
              dataTestId="RentReporting-CreditClimbUpsell"
              onClick={() => emitCreditClimbEnroll(renterProfile)}
            />
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default PaymentsUpsell;
